import React from "react"
import { AnimatePresence, motion } from "motion/react"
import { createPortal } from "react-dom"
import { DialogCustom } from "./DialogCustom"
import { useDialogStates } from "./useDialogCustom"

/**
 * DialogCustom 提供者组件
 * 需要在应用的根组件中渲染此组件，用于管理所有弹窗的渲染
 */
export function DialogCustomProvider(): React.ReactNode {
    const dialogStates = useDialogStates()
        
    // 检查是否有任何弹窗打开
    const hasOpenDialog = Array.from(dialogStates.values()).some((state) => state.visible)

    // 获取所有可见的弹窗，按照添加顺序排序（后添加的在上层）
    const visibleDialogs = Array.from(dialogStates.entries()).filter(
        ([_, state]) => state.visible && state.options,
    )

    // 取最上层可见弹窗的 maskBg 作为全局遮罩背景色来源
    const topMostDialogEntry =
        visibleDialogs.length > 0 ? visibleDialogs[visibleDialogs.length - 1] : undefined
    const effectiveMaskBg = topMostDialogEntry?.[1].options?.maskBg

    // 处理全局mask点击 - 关闭最上层可关闭的弹窗
    const handleGlobalMaskClick = () => {
        // 从后往前找第一个可以通过mask关闭的弹窗
        for (let i = visibleDialogs.length - 1; i >= 0; i--) {
            const [_, state] = visibleDialogs[i]
            const { maskClosable = true } = state.options!

            if (maskClosable) {
                state.reject?.()
                break
            }
        }
    }

    return (
        <>
            {/* 全局mask - 只有一个，所有弹窗共享 */}
            {createPortal(
                <AnimatePresence>
                    {hasOpenDialog && (
                        <motion.div
                            className="global-dialog-mask"
                            onClick={handleGlobalMaskClick}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: effectiveMaskBg ?? "rgba(0, 0, 0, 0.4)",
                                zIndex: 2006, // 比弹窗稍低一点
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        />
                    )}
                </AnimatePresence>,
                document.body,
            )}

            {/* 所有弹窗主体 */}
            {Array.from(dialogStates.entries()).map(([familyName, state]) => {
                // 只有当 options 为 null 时才完全不渲染（已清理的状态）
                if (!state.options) return null

                const { component: Component, ...dialogProps } = state.options

                return (
                    <DialogCustom
                        key={familyName}
                        open={state.visible}
                        title={dialogProps.title}
                        width={dialogProps.width}
                        showIcon={dialogProps.showIcon}
                        showClose={dialogProps.showClose}
                        onCancel={state.reject}
                        icon={dialogProps.icon}
                        style={dialogProps.style}
                    >
                        <Component
                            {...(dialogProps.props || {})}
                            onClose={state.resolve}
                            onNext={dialogProps.onNext || (dialogProps.props as any)?.onNext}
                        />
                    </DialogCustom>
                )
            })}
        </>
    )
}

export default DialogCustomProvider
